import { useEffect, useRef, useState } from 'react';
import type { ILayerProps } from '@/version-types';
import { useFramework } from './framework';

export const useUnit = () => {
    const framework = useFramework();
    const [unit, setUnit] = useState(framework.service.editor.unit);

    useEffect(() => {
        const handle = framework.service.editor.onUnitChange((unit) => setUnit(unit));
        return () => framework.service.events.off(handle);
    }, []);

    return unit;
};

export const useScale = () => {
    const framework = useFramework();
    const [zoom, setZoom] = useState(framework.service.edgelessCanvas.zoom);

    useEffect(() => {
        const handle = framework.service.edgelessCanvas.onZoom((zoom) => setZoom(zoom));
        return () => framework.service.events.off(handle);
    }, []);

    return zoom;
};

export const useTranslateXY = () => {
    const framework = useFramework();
    const { edgelessCanvas, events } = framework.service;

    const [translateXY, setTranslateXY] = useState({
        translateX: edgelessCanvas.translateX,
        translateY: edgelessCanvas.translateY,
    });

    useEffect(() => {
        const handle = edgelessCanvas.onPan((translateX, translateY) => setTranslateXY({ translateX, translateY }));
        return () => events.off(handle);
    }, []);

    return translateXY;
};

export const useGlobalMask = () => {
    const framework = useFramework();
    const [globalMask, setGlobalMask] = useState(framework.service.mask.globalMask);

    useEffect(() => {
        const handle = framework.service.mask.onGlobalMaskChange((globalMask) => setGlobalMask(globalMask));
        return () => framework.service.events.off(handle);
    }, []);

    return globalMask;
};

/** 获取画布模糊半径 */
export const useCanvasBlur = () => {
    const framework = useFramework();
    const { blur: blurService, events } = framework.service;
    const [blur, setBlur] = useState(blurService.getBlur());

    useEffect(() => {
        const handle = blurService.onBlurChange(setBlur);
        return () => events.off(handle);
    }, [blurService, events]);

    return blur;
};

export const useViewSize = () => {
    const framework = useFramework();
    const [viewSize, setViewSize] = useState(framework.service.edgelessCanvas.size);

    useEffect(() => {
        const handle = framework.service.edgelessCanvas.onSizeChange((size) => setViewSize(size));
        return () => framework.service.events.off(handle);
    }, []);

    return viewSize;
};

export const useIsLongImageMode = () => {
    const framework = useFramework();
    const [isLongImageMode, setIsLongImageMode] = useState(framework.service.edgelessCanvas.isLongImageMode);

    useEffect(() => {
        const handle = framework.service.edgelessCanvas.onRenderModeChange(() =>
            setIsLongImageMode(framework.service.edgelessCanvas.isLongImageMode),
        );
        return () => framework.service.events.off(handle);
    }, []);

    return isLongImageMode;
};

/** 获取激活的 frameId */
export const useActiveFrameId = () => {
    const framework = useFramework();
    const [activeFrameId, setActiveFrameId] = useState(framework.service.frame.getActiveFrameId());

    useEffect(() => {
        const handle = framework.service.frame.onActiveIndexChange(() =>
            setActiveFrameId(framework.service.frame.getActiveFrameId()),
        );
        return () => framework.service.events.off(handle);
    }, []);

    return activeFrameId;
};

/** 获取激活的 frame 索引 */
export const useActiveFrameIndex = () => {
    const framework = useFramework();
    const activeFrameId = useActiveFrameId();
    return framework.service.frame.frameIdToIndex(activeFrameId);
};

export const useActiveInfo = () => {
    const framework = useFramework();
    const [activeInfo, setActiveInfo] = useState(framework.service.frame.activeInfo);
    useEffect(() => {
        const handle = framework.service.frame.onActiveInfoChange((activeInfo) => setActiveInfo(activeInfo));
        return () => framework.service.events.off(handle);
    }, []);
    return activeInfo;
};

export const useIsPickStage = () => {
    const framework = useFramework();
    const [isPickStage, setIsPickStage] = useState(framework.service.frame.pickActiveFrame);
    useEffect(() => {
        const handle = framework.service.frame.onPickActiveFrameChange((isPickStage) => setIsPickStage(isPickStage));
        return () => framework.service.events.off(handle);
    }, []);
    return isPickStage;
};

export const useStageList = () => {
    const framework = useFramework();
    const [renderStageList, setRenderStageList] = useState<number[]>(framework.service.frame.getRenderFrameIds());

    useEffect(() => {
        const handler = framework.service.frame.onRerenderAll((renderStageList) => {
            setRenderStageList([...renderStageList]);
        });
        return () => {
            framework.service.events.off(handler);
        };
    }, []);

    return renderStageList;
};

export const useIsBasicOperate = () => {
    const framework = useFramework();
    const [isBasicOperate, setIsBasicOperate] = useState(false);

    useEffect(() => {
        const handle = framework.service.editor.onIsBaseOperateChange((isBaseOperate) =>
            setIsBasicOperate(isBaseOperate),
        );

        return () => framework.service.events.off(handle);
    }, []);

    return isBasicOperate;
};

export const useIsDisableMove = () => {
    const framework = useFramework();
    const [isDisableMove, setIsDisableMove] = useState(framework.service.editor.disableMove);
    useEffect(() => {
        const handle = framework.service.editor.onDisableMoveChange((disableMove) => setIsDisableMove(disableMove));
        return () => framework.service.events.off(handle);
    }, []);
    return isDisableMove;
};
export const useIsDisableResize = () => {
    const framework = useFramework();
    const [isDisableResize, setIsDisableResize] = useState(framework.service.editor.disableResize);
    useEffect(() => {
        const handle = framework.service.editor.onDisableResizeChange((disableResize) =>
            setIsDisableResize(disableResize),
        );
        return () => framework.service.events.off(handle);
    }, []);
    return isDisableResize;
};
export const useIsDisableRotate = () => {
    const framework = useFramework();
    const [isDisableRotate, setIsDisableRotate] = useState(framework.service.editor.disableRotate);
    useEffect(() => {
        const handle = framework.service.editor.onDisableRotateChange((disableRotate) =>
            setIsDisableRotate(disableRotate),
        );
        return () => framework.service.events.off(handle);
    }, []);
    return isDisableRotate;
};
export const useIsDisableScale = () => {
    const framework = useFramework();
    const [isDisableScale, setIsDisableScale] = useState(framework.service.editor.disableScale);
    useEffect(() => {
        const handle = framework.service.editor.onDisableScaleChange((disableScale) => setIsDisableScale(disableScale));
        return () => framework.service.events.off(handle);
    }, []);
    return isDisableScale;
};

/**
 * 获取激活图层数据
 *
 * 组件重新挂载时先读取当前选中态，避免仅依赖后续事件导致已有激活框不渲染。
 *
 * @returns 当前激活图层及组内选中图层
 */
export const useActiveLayerData = () => {
    const framework = useFramework();
    const [activeData, setActiveData] = useState<{
        layer: ILayerProps | null;
        pickLayer: ILayerProps | null;
    } | null>(() => {
        const layer = framework.service.frame.getActiveLayer();
        if (!layer) return null;
        const pickLayer = framework.service.frame.getActivePickLayer(false);
        return { layer, pickLayer: layer === pickLayer ? null : pickLayer };
    });

    useEffect(() => {
        const handle = framework.service.frame.onActiveLayerDataUpdate((_, pickLayer) => {
            const activeLayer = framework.service.frame.getActiveLayer();
            setActiveData({ layer: activeLayer, pickLayer });
        });
        return () => {
            framework.service.events.off(handle);
        };
    }, []);

    return activeData;
};

/** 获取是否禁用拾取 */
export const useIsDisablePick = () => {
    const framework = useFramework();
    const [isDisablePick, setIsDisablePick] = useState(framework.service.editor.disablePick);
    useEffect(() => {
        const handle = framework.service.editor.onDisablePickChange((disablePick) => setIsDisablePick(disablePick));
        return () => framework.service.events.off(handle);
    }, []);
    return isDisablePick;
};

/** 获取是否禁用操作 */
export const useIsDisableOperate = () => {
    const framework = useFramework();
    const [isDisableOperate, setIsDisableOperate] = useState(framework.service.editor.disableOperate);
    useEffect(() => {
        const handle = framework.service.editor.onDisableOperateChange((disableOperate) =>
            setIsDisableOperate(disableOperate),
        );
        return () => framework.service.events.off(handle);
    }, []);
    return isDisableOperate;
};

/** 获取是否正在执行鼠标框选 */
export const useIsMouseSelect = () => {
    const framework = useFramework();
    const [isMouseSelect, setIsMouseSelect] = useState(framework.service.editor.isMouseSelect);
    useEffect(() => {
        const handle = framework.service.editor.onIsMouseSelectChange((isMouseSelect) =>
            setIsMouseSelect(isMouseSelect),
        );
        return () => framework.service.events.off(handle);
    }, []);
    return isMouseSelect;
};

export const useMinZoom = () => {
    const framework = useFramework();
    const [minZoom, setMinZoom] = useState(framework.service.edgelessCanvas.minZoom);
    useEffect(() => {
        const handle = framework.service.edgelessCanvas.onMinZoomChange((minZoom) => setMinZoom(minZoom));
        return () => framework.service.events.off(handle);
    }, []);
    return minZoom;
};

export const useMaxZoom = () => {
    const framework = useFramework();
    const [maxZoom, setMaxZoom] = useState(framework.service.edgelessCanvas.maxZoom);
    useEffect(() => {
        const handle = framework.service.edgelessCanvas.onMaxZoomChange((maxZoom) => setMaxZoom(maxZoom));
        return () => framework.service.events.off(handle);
    }, []);
    return maxZoom;
};

export const useLoadingMap = () => {
    const framework = useFramework();
    const [loadingMap, setLoadingMap] = useState(framework.service.loading.loadingMap);
    useEffect(() => {
        const handle = framework.service.loading.onLoadingMapChange((loadingMap) => setLoadingMap(loadingMap));
        return () => framework.service.events.off(handle);
    }, []);
    return loadingMap;
};

/** 获取进度映射 */
export const useProgressMap = () => {
    const framework = useFramework();
    const [progressMap, setProgressMap] = useState(framework.service.loading.progressMap);
    useEffect(() => {
        const handle = framework.service.loading.onProgressMapChange((progressMap) => setProgressMap(progressMap));
        return () => framework.service.events.off(handle);
    }, []);
    return progressMap;
};

export const useShineMap = () => {
    const framework = useFramework();
    const [shineMap, setShineMap] = useState(framework.service.shine.shineMap);
    useEffect(() => {
        const handle = framework.service.shine.onShineMapChange((shineMap) => setShineMap(shineMap));
        return () => framework.service.events.off(handle);
    }, []);
    return shineMap;
};
/**
 * 修改图层定位
 * @param layerId - 图层ID
 * @param byBaseOperate - 是否通过基础操作触发
 * @returns
 */
export const useLayerTranslate = (layerId?: string, byBaseOperate = false) => {
    const refNode = useRef<HTMLDivElement>(null);
    const framework = useFramework();
    const { edgelessCanvas, events, flatLayer, frame, transform } = framework.service;
    const [_, refresh] = useState<number>(0);

    useEffect(() => {
        if (!layerId) return;

        const updateLayerTransform = (updateSize = false) => {
            if (updateSize) {
                refresh((prev) => prev + 1);
                return;
            }
            const node = refNode.current;
            if (!node) return;
            const layer = flatLayer.getLayer(layerId);
            if (!layer) return;
            const { left, rotate } = layer;
            const offset = transform.calcLayerOffsetTop(layer);
            const top = layer.top + offset;
            const scale = edgelessCanvas.zoom;
            const translateX = edgelessCanvas.translateX;
            const translateY = edgelessCanvas.translateY;
            node.style.transform = `translate(${left * scale + translateX}px, ${top * scale + translateY}px) rotate(${rotate}deg)`;
        };
        const handler = byBaseOperate
            ? frame.onLayerMoveOrRotate((id) => {
                  if (id !== layerId) return;
                  updateLayerTransform();
              })
            : flatLayer.onChange(() => {
                  updateLayerTransform(true);
              }, layerId);
        const layoutHandler = frame.onFrameLayoutComplete((renderFrameIds) => {
            const frameId = frame.getLayerFrameId(layerId);
            if (frameId === undefined || !renderFrameIds.includes(frameId)) return;
            updateLayerTransform();
        });

        return () => {
            events.off(handler);
            events.off(layoutHandler);
        };
    }, [byBaseOperate, edgelessCanvas, events, flatLayer, frame, layerId, transform]);

    return refNode;
};

export const useHoverId = () => {
    const framework = useFramework();
    const [hoverId, setHoverId] = useState(framework.service.frame.hoverId);
    useEffect(() => {
        const handle = framework.service.frame.onHoverIdChange((hoverId) => setHoverId(hoverId));
        return () => framework.service.events.off(handle);
    }, []);
    return hoverId;
};
